import type {
  GameMode,
  Hero,
  LobbyDisbandedPayload,
  MatchFoundPayload,
  MultiplayerLobbyState,
  GameStartPayload,
  MultiplayerGameDelta,
  GameEndPayload,
  OpponentDisconnectedPayload,
  PlayerAction,
  PlacementEntry,
} from '@roe/shared';
import { SocketService } from './SocketService.js';

type Listener<T> = (payload: T) => void;

interface RegisteredListener {
  event: string;
  fn: Listener<unknown>;
}

/** Shape of the `multiplayer:invite-received` payload. Mirrored here so
 *  MultiplayerService doesn't have to import the Phaser-coupled InviteToast
 *  type (which lives in the UI layer). */
export interface InvitePayload {
  inviteId: string;
  fromUserId: string;
  fromName: string;
  mode: GameMode;
  expiresAt: number;
}

/**
 * Thin bridge between Socket.IO multiplayer events and Phaser scenes.
 * Reuses the existing SocketService connection — never disconnects it.
 */
export class MultiplayerService {
  static _instance: MultiplayerService | null = null;
  private listeners: RegisteredListener[] = [];
  /** Targets with an in-flight `sendInviteWithFeedback` call. Prevents two
   *  concurrent invites to the same person from cross-settling each other's
   *  ack listeners (the wire payload only carries `targetUserId`, no per-send
   *  nonce, so a second concurrent send to the same target would steal the
   *  first's `multiplayer:invite-sent` event). */
  private inflightInviteTargets = new Set<string>();
  /** Current MP menu state — set by `enterMenu`, cleared by `leaveMenu`. The
   *  server's `socket.data.mpMenuMode` is wiped on disconnect; on every
   *  socket reconnect we re-emit `multiplayer:enter-menu` so the user stays
   *  reachable for incoming invites. Without this, every server restart (or
   *  transient network blip) silently broke invite delivery for live lobby
   *  clients until they manually left and re-entered the lobby. */
  private currentMpMenu: { mode: GameMode; saveSlot: number } | null = null;
  /** Active scene-level invite handler. Set by whichever scene called
   *  `bindInviteToast(fn)` last; cleared by `unbindInviteToast`. The service
   *  registers ONE socket-level `multiplayer:invite-received` listener at
   *  construction; routing to the current scene is handled here. This
   *  removes the previous "handler lives only in MultiplayerLobbyBase" gap
   *  that meant a friend on MenuScene/HeroScene/BattleScene never saw the
   *  toast even when the server emitted the event. */
  private inviteHandler: ((payload: InvitePayload) => void) | null = null;

  private constructor() {
    // Re-bind everything that depends on the live socket on every connect
    // (initial + every reconnect + new socket after logout/login). Codex
    // stop-time review caught that the previous one-shot bind in the
    // constructor was stale after `SocketService.disconnect()` discarded
    // the socket and `connect()` created a fresh one — invites stopped
    // arriving until full page reload.
    SocketService.getInstance().addReconnectListener(() => {
      const sock = SocketService.getInstance().getSocket();
      console.debug('[mp-invite-debug] reconnect-listener fired', {
        hasSocket: !!sock,
        socketId: (sock as { id?: string } | null)?.id,
        hasCurrentMpMenu: this.currentMpMenu !== null,
      });
      if (!sock) return;
      // Restore enter-menu state if the user was in a lobby.
      if (this.currentMpMenu) sock.emit('multiplayer:enter-menu', this.currentMpMenu);
      // Re-bind the persistent invite-received listener so it lives on the
      // CURRENT socket, not whichever socket existed at construction. Idempotent:
      // remove any prior binding first so we don't accumulate duplicates if the
      // listener fires on the same socket more than once (defensive).
      sock.off('multiplayer:invite-received', this._inviteRouter);
      sock.on('multiplayer:invite-received', this._inviteRouter);
    });
    // Bind once eagerly so an already-connected socket receives invites
    // without waiting for the next reconnect. Reconnect rebinds will replace
    // this binding when the socket changes.
    const socket = SocketService.getInstance().getSocket();
    console.debug('[mp-invite-debug] MultiplayerService constructor', {
      socketAvailable: !!socket,
      socketId: (socket as { id?: string } | null)?.id,
    });
    socket?.on('multiplayer:invite-received', this._inviteRouter);
  }

  /** Bound method reference so add/remove listener pairs match across calls.
   *  Routes invite-received events to the currently-bound scene handler;
   *  falls back to `fallbackInviteHandler` if no scene has called
   *  `bindInviteToast`. The fallback is wired at boot from main.ts so the
   *  toast renders on whatever scene is active even when the user is on
   *  HeroScene/MerchantScene/etc. (those scenes don't bind explicitly). */
  private _inviteRouter = (payload: InvitePayload): void => {
    console.debug('[mp-invite-debug] _inviteRouter fired', {
      inviteId: payload.inviteId,
      fromUserId: payload.fromUserId,
      fromName: payload.fromName,
      hasSceneHandler: this.inviteHandler !== null,
      hasFallback: this.fallbackInviteHandler !== null,
    });
    if (this.inviteHandler) {
      this.inviteHandler(payload);
      return;
    }
    if (this.fallbackInviteHandler) {
      this.fallbackInviteHandler(payload);
    } else {
      console.debug('[mp-invite-debug] DROPPED — no handler bound', payload.inviteId);
    }
  };

  /** Fallback handler called when no scene called `bindInviteToast`. Set
   *  once at boot — typically wired to `InviteToast.getInstance().enqueue`. */
  private fallbackInviteHandler: ((payload: InvitePayload) => void) | null = null;

  setFallbackInviteHandler(fn: (payload: InvitePayload) => void): void {
    this.fallbackInviteHandler = fn;
  }

  static getInstance(): MultiplayerService {
    if (!MultiplayerService._instance) {
      MultiplayerService._instance = new MultiplayerService();
    }
    return MultiplayerService._instance;
  }

  // ──────────────────────────────────────────────
  // Outgoing events
  // ──────────────────────────────────────────────

  joinQueue(mode: GameMode, opts?: { stageLevel?: number; saveSlot?: number; pvpHeroes?: import('@roe/shared').PvpHeroData[]; coopSaveSlot?: number }): void {
    SocketService.getInstance().getSocket()?.emit('join-queue', { mode, ...opts });
  }

  leaveQueue(): void {
    SocketService.getInstance().getSocket()?.emit('leave-queue');
  }

  createInvite(mode: GameMode, opts?: { coopSaveSlot?: number }): void {
    SocketService.getInstance().getSocket()?.emit('create-invite', { mode, ...opts });
  }

  // ── Direct-invite (push-style) + MP-menu presence ─────────────────────────

  /** Bind a scene-level handler for incoming `multiplayer:invite-received`
   *  events. The MultiplayerService keeps ONE persistent socket listener;
   *  whichever scene called `bindInviteToast` most recently receives the
   *  next invite. Each scene calls this in `create()` and pairs it with
   *  `unbindInviteToast(fn)` on shutdown. Removes the previous gap where
   *  invites only worked when the recipient was already inside an MP lobby
   *  scene (because the listener was scene-local). */
  bindInviteToast(fn: (payload: InvitePayload) => void): void {
    console.debug('[mp-invite-debug] bindInviteToast', {
      previouslyBound: this.inviteHandler !== null,
    });
    this.inviteHandler = fn;
  }

  /** Clear the scene-level invite handler. Caller passes the same fn it
   *  registered to avoid races where Scene B's bind clobbers Scene A's
   *  handler before Scene A's shutdown unbinds. */
  unbindInviteToast(fn: (payload: InvitePayload) => void): void {
    const matched = this.inviteHandler === fn;
    console.debug('[mp-invite-debug] unbindInviteToast', { matched });
    if (matched) this.inviteHandler = null;
  }

  /** Tell the server we're in the multiplayer menu — gates incoming invites.
   *  Also tracks state so a socket reconnect re-emits this automatically (the
   *  server's per-socket `mpMenuMode` is reset on every disconnect). */
  enterMenu(mode: GameMode, saveSlot: number): void {
    this.currentMpMenu = { mode, saveSlot };
    SocketService.getInstance().getSocket()?.emit('multiplayer:enter-menu', { mode, saveSlot });
  }

  /** Clear our MP-menu presence (shutdown / leave-lobby path). Stops the
   *  reconnect re-emit so we don't surface as "in MP menu" after the user
   *  has left the lobby. */
  leaveMenu(): void {
    this.currentMpMenu = null;
    SocketService.getInstance().getSocket()?.emit('multiplayer:leave-menu');
  }

  sendInvite(targetUserId: string): void {
    SocketService.getInstance().getSocket()?.emit('multiplayer:invite', { targetUserId });
  }

  /** Ack-driven invite send. Resolves to `{ ok: true, inviteId }` when the
   *  server emits `multiplayer:invite-sent` for this target, or
   *  `{ ok: false, message }` when the server emits a `multiplayer:invite-error`
   *  with NO `inviteId` (send-side error such as `target-not-in-menu`,
   *  `target-offline`, or `rate-limit`). Errors WITH an `inviteId` belong to a
   *  different active invite and are ignored. Times out after 5 s with a
   *  generic failure message so the caller never hangs forever.
   *
   *  Replaces the optimistic "Invite sent" toast pattern that previously
   *  swallowed send-side rejections — see SocialHub.ts. */
  sendInviteWithFeedback(targetUserId: string): Promise<
    | { ok: true; inviteId: string }
    | { ok: false; message: string }
  > {
    const socket = SocketService.getInstance().getSocket();
    if (!socket) {
      console.warn('[mp-invite] sendInviteWithFeedback: no socket');
      return Promise.resolve({ ok: false, message: 'Not connected to server.' });
    }
    console.log('[mp-invite] sender → emit', { targetUserId, socketId: (socket as { id?: string }).id });

    // Reject concurrent sends to the same target. The server's invite-sent /
    // invite-error events carry only `targetUserId` (no per-call nonce), so a
    // second concurrent send to the same person would steal the first's
    // listener and both promises would resolve from one server event. UX-wise
    // a second concurrent invite to the same target is also wrong (the user
    // already has one outstanding) — surfacing as an inline error is correct.
    if (this.inflightInviteTargets.has(targetUserId)) {
      return Promise.resolve({ ok: false, message: 'Invite already in flight to this user.' });
    }
    this.inflightInviteTargets.add(targetUserId);

    return new Promise((resolve) => {
      let settled = false;
      const cleanup = (): void => {
        socket.off('multiplayer:invite-sent', onSent);
        socket.off('multiplayer:invite-error', onError);
        clearTimeout(timer);
        this.inflightInviteTargets.delete(targetUserId);
      };

      const onSent = (payload: unknown): void => {
        if (settled) return;
        const p = payload as { inviteId?: string; targetUserId?: string };
        if (p?.targetUserId !== targetUserId) return; // belongs to another send
        settled = true;
        cleanup();
        console.log('[mp-invite] sender ← invite-sent ack', { targetUserId, inviteId: p.inviteId });
        resolve({ ok: true, inviteId: p.inviteId ?? '' });
      };

      const onError = (payload: unknown): void => {
        if (settled) return;
        const p = payload as { code?: string; message?: string; inviteId?: string; targetUserId?: string };
        if (p?.inviteId) return; // tied to a different invite, not ours
        // If the server stamps targetUserId on send-side errors (newer build),
        // require a match. Older servers don't include it — accept if absent.
        if (p?.targetUserId !== undefined && p.targetUserId !== targetUserId) return;
        settled = true;
        cleanup();
        console.warn('[mp-invite] sender ← invite-error', { targetUserId, code: p?.code, message: p?.message });
        resolve({ ok: false, message: p?.message ?? 'Could not send invite.' });
      };

      socket.on('multiplayer:invite-sent', onSent);
      socket.on('multiplayer:invite-error', onError);

      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve({ ok: false, message: 'Invite timed out.' });
      }, 5000);

      socket.emit('multiplayer:invite', { targetUserId });
    });
  }

  acceptInvite(inviteId: string): void {
    SocketService.getInstance().getSocket()?.emit('multiplayer:invite-accept', { inviteId });
  }

  declineInvite(inviteId: string): void {
    SocketService.getInstance().getSocket()?.emit('multiplayer:invite-decline', { inviteId });
  }

  // ── Co-op save management (HTTP) ──────────────────────────────────────────

  async fetchCoopSaves(): Promise<import('@roe/shared').CoopSaveInfo[]> {
    const { AuthService } = await import('./AuthService.js');
    const token = AuthService.getInstance().getToken();
    const res = await fetch('/api/coop-saves', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Failed to fetch co-op saves');
    return res.json() as Promise<import('@roe/shared').CoopSaveInfo[]>;
  }

  /** Create or upsert a Coop save slot. Accepts either v1 legacy class strings
   *  (back-compat) or canonical v2 `CoopSaveHeroEntry[]`. v1 input is coerced
   *  to v2 via `normalizeCoopSaveHeroes`; the wire body always carries
   *  `schemaVersion: 2` so server reads always have heroKey when the original
   *  caller provided it (Bug B fix — partner sprite previously fell back to
   *  generic class because heroKey was dropped at this layer). */
  async createCoopSave(
    slot: number,
    heroes: import('@roe/shared').CoopSaveHeroEntry[] | string[] = [],
  ): Promise<{ slot: number }> {
    const { AuthService } = await import('./AuthService.js');
    const { normalizeCoopSaveHeroes } = await import('@roe/shared');
    const token = AuthService.getInstance().getToken();
    const v2Heroes = normalizeCoopSaveHeroes(heroes);
    const res = await fetch(`/api/coop-saves/${slot}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ stage: 1, wave: 1, schemaVersion: 2, heroes: v2Heroes }),
    });
    if (!res.ok) throw new Error('Failed to create co-op save');
    return { slot };
  }

  async deleteCoopSave(slot: number): Promise<void> {
    const { AuthService } = await import('./AuthService.js');
    const token = AuthService.getInstance().getToken();
    await fetch(`/api/coop-saves/${slot}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  }

  /** Join an existing 6-char invite room. The optional `coopSaveSlot` carries
   *  the joiner's chosen Coop save so the server can pre-resolve their heroes
   *  (without it the lobby would later stall at countdown 0 because the
   *  server has no slot to look up the joiner's CoopSave). */
  joinInvite(code: string, coopSaveSlot?: number): void {
    SocketService.getInstance().getSocket()?.emit('join-invite', { code, coopSaveSlot });
  }

  selectHeroes(roomId: string, heroes: Hero[]): void {
    SocketService.getInstance().getSocket()?.emit('select-heroes', { roomId, heroes });
  }

  toggleReady(roomId: string): void {
    SocketService.getInstance().getSocket()?.emit('toggle-ready', { roomId });
  }

  leaveLobby(roomId: string): void {
    SocketService.getInstance().getSocket()?.emit('leave-lobby', { roomId });
  }

  sendAction(action: PlayerAction): void {
    SocketService.getInstance().getSocket()?.emit('player-action', action);
  }

  submitPlacement(placements: PlacementEntry[]): void {
    SocketService.getInstance().getSocket()?.emit('submit-placement', { placements });
  }

  // ──────────────────────────────────────────────
  // Incoming event registration
  // ──────────────────────────────────────────────

  onMatchFound(cb: Listener<MatchFoundPayload>): void {
    this._on('match-found', cb as Listener<unknown>);
  }

  onLobbyUpdate(cb: Listener<MultiplayerLobbyState>): void {
    this._on('lobby-state-update', cb as Listener<unknown>);
  }

  onGameStart(cb: Listener<GameStartPayload>): void {
    this._on('game-start', cb as Listener<unknown>);
  }

  onGameStateUpdate(cb: Listener<MultiplayerGameDelta>): void {
    this._on('game-state-update', cb as Listener<unknown>);
  }

  onGameEnd(cb: Listener<GameEndPayload>): void {
    this._on('game-end', cb as Listener<unknown>);
  }

  onOpponentDisconnect(cb: Listener<OpponentDisconnectedPayload>): void {
    this._on('opponent-disconnected', cb as Listener<unknown>);
  }

  onLobbyDisbanded(cb: Listener<LobbyDisbandedPayload>): void {
    this._on('lobby-disbanded', cb as Listener<unknown>);
  }

  onPlacementPhaseEnd(cb: () => void): void {
    this._on('placement-phase-end', cb as Listener<unknown>);
  }

  // ── Direct-invite listeners ───────────────────────────────────────────────

  onInviteReceived(cb: Listener<{ inviteId: string; fromUserId: string; fromName: string; mode: GameMode; expiresAt: number }>): void {
    this._on('multiplayer:invite-received', cb as Listener<unknown>);
  }

  onInviteSent(cb: Listener<{ inviteId: string; targetUserId: string }>): void {
    this._on('multiplayer:invite-sent', cb as Listener<unknown>);
  }

  onInviteError(cb: Listener<{ code: string; message: string; inviteId?: string }>): void {
    this._on('multiplayer:invite-error', cb as Listener<unknown>);
  }

  onInviteDeclined(cb: Listener<{ byUserId: string }>): void {
    this._on('multiplayer:invite-declined', cb as Listener<unknown>);
  }

  onInviteExpired(cb: Listener<{ inviteId: string }>): void {
    this._on('multiplayer:invite-expired', cb as Listener<unknown>);
  }

  // ──────────────────────────────────────────────
  // Cleanup (removes only multiplayer listeners)
  // ──────────────────────────────────────────────

  cleanup(): void {
    const socket = SocketService.getInstance().getSocket();
    for (const { event, fn } of this.listeners) {
      socket?.off(event, fn);
    }
    this.listeners = [];
  }

  /** Remove a specific listener without affecting others. Used by scoped adapters. */
  offListener(event: string, fn: Listener<unknown>): void {
    SocketService.getInstance().getSocket()?.off(event, fn);
    this.listeners = this.listeners.filter(l => !(l.event === event && l.fn === fn));
  }

  // ──────────────────────────────────────────────
  // Internal
  // ──────────────────────────────────────────────

  private _on(event: string, fn: Listener<unknown>): void {
    SocketService.getInstance().getSocket()?.on(event, fn);
    this.listeners.push({ event, fn });
  }
}
